import { Link } from "react-router-dom";
import { useEffect } from "react";
import { HeroButton, GlassButton } from "@/components/ui/button-variants";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Heart, Sparkles, Users, MapPin, Camera, MessageCircle, Shield } from "lucide-react";
import heroImage from "@/assets/hero-astropet.jpg";
import Footer from "@/components/Layout/Footer";

const Welcome = () => {
  // Set document title and meta description for SEO
  useEffect(() => {
    document.title = 'AstroPet - Social Network per Amanti degli Animali';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Unisciti ad AstroPet, il social network dedicato agli amanti degli animali. Connetti con altri proprietari di pet, condividi foto, trova servizi per animali e gestisci il passaporto digitale del tuo pet.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Unisciti ad AstroPet, il social network dedicato agli amanti degli animali. Connetti con altri proprietari di pet, condividi foto, trova servizi per animali e gestisci il passaporto digitale del tuo pet.';
      document.head.appendChild(meta);
    }
    
    // Set canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://astropet.it/welcome');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://astropet.it/welcome';
      document.head.appendChild(link);
    }
  }, []);

  const features = [{
    icon: Heart,
    title: "Pet Social Network",
    description: "Connetti con amanti degli animali e condividi le avventure dei tuoi pet"
  }, {
    icon: Camera,
    title: "Profili Pet & Post",
    description: "Crea bellissimi profili per i tuoi pet e condividi momenti memorabili"
  }, {
    icon: MapPin,
    title: "Mappa Interattiva",
    description: "Scopri servizi pet, eventi e incontra altri proprietari nelle vicinanze"
  }, {
    icon: MessageCircle,
    title: "Chat Community",
    description: "Connetti e chatta con altri appassionati di animali in tempo reale"
  }, {
    icon: Users,
    title: "Adozioni Pet",
    description: "Aiuta i pet a trovare case amorevoli attraverso la nostra piattaforma"
  }, {
    icon: Shield,
    title: "Passaporto Digitale",
    description: "Tieni traccia della salute, vaccinazioni e documenti del tuo pet"
  }];
  return <div className="min-h-screen gradient-secondary">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={heroImage} alt="AstroPet Hero" className="w-full h-full object-cover opacity-60 py-0" />
          <div className="absolute inset-0 gradient-glow" />
        </div>
        
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="flex items-center justify-center mb-6">
                <img src="/lovable-uploads/5e002d38-22aa-49af-85ae-34109191ea66.png" alt="AstroPet Logo" className="w-32 h-32" />
              </div>
              <h1 className="text-6xl gradient-text font-space mx-0 my-[25px] px-0 py-[5px] font-bold md:text-8xl">
                AstroPet
              </h1>
              <p className="text-xl md:text-2xl text-foreground/80 max-w-2xl mx-auto py-[11px]">
                Il social network del futuro dedicato ai tuoi amici a quattro zampe. 
                Connetti, condividi e esplora l'universo pet.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/auth">
                <HeroButton size="lg" className="shadow-2xl">
                  Inizia Ora
                </HeroButton>
              </Link>
              <GlassButton size="lg">
                Scopri di Più
              </GlassButton>
            </div>
          </div>
        </div>

        {/* Floating Animation Elements */}
        <div className="absolute top-20 left-10 w-4 h-4 rounded-full bg-primary animate-pulse opacity-60" />
        <div className="absolute top-40 right-20 w-6 h-6 rounded-full bg-secondary animate-bounce opacity-40" />
        <div className="absolute bottom-32 left-1/4 w-3 h-3 rounded-full bg-accent animate-ping opacity-50" />
      </section>


      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-6">
              Funzionalità Stellari
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Scopri tutto quello che AstroPet ha da offrire per te e i tuoi pet
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => <Card key={index} className="glass shadow-card border-white/10 transition-smooth hover:scale-105 hover:glow-primary">
                <CardHeader className="text-center">
                  <div className="mx-auto w-16 h-16 rounded-full gradient-primary flex items-center justify-center mb-4 glow-primary">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl gradient-text">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-center text-muted-foreground">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <Card className="max-w-4xl mx-auto glass shadow-card border-white/10">
          <CardContent className="p-12 text-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h3 className="text-3xl md:text-4xl font-bold gradient-text">
                  Pronto a Decollare?
                </h3>
                <p className="text-xl text-muted-foreground">
                  Unisciti alla community di AstroPet e inizia a condividere le avventure dei tuoi pet.
                </p>
              </div>
              
              <Link to="/auth">
                <HeroButton size="lg" className="shadow-2xl my-[25px]">
                  <Heart className="w-6 h-6 mr-2" fill="white" />
                  Registrati Ora
                </HeroButton>
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <Footer />
    </div>;
};
export default Welcome;